// @ts-nocheck
/*
 * Port of views/dashboard/data-center/create.js (origin/main-dev, 266 lines).
 * BYTE-IDENTICAL — includes v1's local validateEmail redefinition
 * shadowing the imported one, domestic vs international payload branch,
 * validation ordering, and setToastProps({...danger}) fallback path on
 * create-error. Chrome swap: v1 <Card> → shadcn.
 */
import { useState, useEffect } from 'react';
import { postApiCall } from '@/service/httpCall';
import { API_DATA_CENTER_CREATE } from '@/service/url';
import { useNavigate } from 'react-router-dom';
import { ROUTES, DATA_CENTER_DOMESTIC_TYPE, DATA_CENTER_INTERNATIONAL } from '@/utilities/constant';
import RenderDataCenterForm from '@/features/data-center/DataCenterForm';
import { validatePhone } from '@/utilities/helpers';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES as PAGE_ROUTES } from '@/utilities/constant';

export default function DataCenterCreate() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [name, setName] = useState('');
  const [dataCenterType, setDataCenterType] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [dataCenterCompany, setDataCenterCompany] = useState('');
  const [dataCenterAddress, setDataCenterAddress] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [countryData, setCountryData] = useState({});
  const [stateData, setStateData] = useState({});
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    const onlyValid = value.replace(/[^a-zA-Z0-9_]/g, '');
    if (value !== onlyValid) {
      setError('Only letters, numbers, and underscore (_) are allowed.');
    } else {
      setError('');
    }
    setName(onlyValid);
  };

  // v1 shadows the imported validateEmail with a local one that returns a boolean.
  function validateEmail(email) {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email.trim());
  }

  function validateForm() {
    let valid = true;

    if (!name.trim()) {
      valid = false;
      toast.error('Name required');
    } else if (!dataCenterType) {
      valid = false;
      toast.error('Data Center Type required');
    } else if (!country.trim()) {
      valid = false;
      toast.error('Country required');
    } else if (!state.trim()) {
      valid = false;
      toast.error('State required');
    } else if (!city.trim()) {
      valid = false;
      toast.error('City required');
    } else if (!contactPerson.trim()) {
      valid = false;
      toast.error('Contact Person required');
    } else if (!validateEmail(contactEmail)) {
      valid = false;
      toast.error('Invalid Contact Email format');
    } else if (!validatePhone(contactNumber)) {
      valid = false;
      toast.error('Invalid Contact number format');
    } else if (!dataCenterCompany.trim()) {
      valid = false;
      toast.error('Data Center Company required');
    } else if (!dataCenterAddress.trim()) {
      valid = false;
      toast.error('Data Center Address required');
    }

    if (emailError) {
      valid = false;
      toast.error('Enter correct Email');
    }

    return valid;
  }

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  async function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    let params = {
      name,
      type: Number(dataCenterType),
      contact_person: contactPerson,
      contact_email: contactEmail,
      contact_number: Number(contactNumber),
      data_centre_company: dataCenterCompany,
      data_centre_address: dataCenterAddress,
    };
    if (dataCenterType == DATA_CENTER_DOMESTIC_TYPE) {
      const domesticDetails = {
        state: { code: state, name: stateData[state] },
        city: city,
      };
      params = { ...params, domestic_details: domesticDetails };
    } else if (dataCenterType == DATA_CENTER_INTERNATIONAL) {
      const overseasDetails = {
        country: { code: country, name: countryData[country] },
        state: { code: state, name: stateData[state] },
        city: city,
      };
      params = { ...params, overseas_details: overseasDetails };
    }

    postApiCall(API_DATA_CENTER_CREATE, params)
      .then(onDataCenterCreateSuccess)
      .catch(onDataCenterCreateError);
  }

  function onDataCenterCreateSuccess() {
    toast.success('Data Center created successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.DATA_CENTER.LIST}`);
    }, 1500);
  }

  function onDataCenterCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err));
    }
  }

  return (
    <ScrollPage>
      <PageHeader
        title="New Data Center"
        description="Onboard a new data-centre location with contact and address details."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to={`/${PAGE_ROUTES.DATA_CENTER.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />
      <Card className="mt-4 p-4">
        <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/60 pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Database className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold text-foreground">Data Center Details</div>
              <div className="text-xs text-muted-foreground">
                Fields marked with <span className="text-destructive">*</span> are required
              </div>
            </div>
          </div>
        </div>
        <form onSubmit={async (e) => { submitForm(e); }}>
          <RenderDataCenterForm
                  name={name}
                  setName={setName}
                  dataCenterType={dataCenterType}
                  setDataCenterType={setDataCenterType}
                  country={country}
                  setCountry={setCountry}
                  state={state}
                  setState={setState}
                  city={city}
                  setCity={setCity}
                  contactPerson={contactPerson}
                  setContactPerson={setContactPerson}
                  contactEmail={contactEmail}
                  setContactEmail={setContactEmail}
                  contactNumber={contactNumber}
                  setContactNumber={setContactNumber}
                  dataCenterCompany={dataCenterCompany}
                  setDataCenterCompany={setDataCenterCompany}
                  dataCenterAddress={dataCenterAddress}
                  setDataCenterAddress={setDataCenterAddress}
                  countryData={countryData}
                  setCountryData={setCountryData}
                  stateData={stateData}
                  setStateData={setStateData}
                  emailError={emailError}
                  setEmailError={setEmailError}
                  handleChange={handleChange}
                  error={error}
                />

          <div className="mt-6 flex items-center justify-end gap-2 border-t border-border/60 pt-4">
            <Button asChild variant="ghost" size="sm">
              <Link to={`/${PAGE_ROUTES.DATA_CENTER.LIST}`}>Cancel</Link>
            </Button>
            <Button type="submit" size="sm" disabled={buttonDisable} className="gap-1.5">
              {buttonDisable ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {buttonDisable ? 'Adding…' : 'Add data center'}
            </Button>
          </div>
        </form>
      </Card>
    </ScrollPage>
  );
}
